(ns frontend.view.grid.transaction-processes-grid.view
  "Transaction-process list at /transaction-processes, built on the shared legacy
   grid engine (frontend.react.virtualized.scrollable-grid) like the KYC and
   counterparty-diligence grids: sortable / resizable / reorderable columns,
   column show/hide, quick-search, per-user persisted layout, per-column filters,
   named views and bulk delete. Rows come from the session's transaction-process
   entity type, access-filtered client-side; the template-derived stage/transaction-type
   labels and template name are pre-injected as :display-data in events.cljs."
  (:require
   [common.attribute :as attribute]
   [common.fields.table-column-widths :as widths]
   [common.localization :refer [tr]]
   [common.schema.registry :as registry]
   [frontend.db :as app-db]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.components :as grid-components]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.grid-filters.view :as grid-filters]
   [frontend.view.grid.common.components :as components]
   [frontend.view.grid.transaction-processes-grid.db :as db]
   [frontend.view.grid.transaction-processes-grid.events :as events]
   [frontend.view.named-view.events :as named-view-events]
   [frontend.view.not-found :as not-found]
   [frontend.ux-overhaul.listing.transaction-process.view :as transaction-process-listing]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.view.transaction-process.create-modal :as create-modal]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (partial grid/update-width! (events/setup-args) recompute-grid-sizes! hover-atom attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn toolbar-buttons-renderer
  "Rendered in the selection action bar when one or more rows are selected."
  []
  (let [selected (grid/get-selected-items (events/setup-args))]
    [:div
     {:class "flex gap-2"}
     (if (session/is-read-only-user?)
       [:span (tr :no-grid-actions-for-read-only-user)]
       [components/toolbar-delete-button
        {:allow-non-admin-delete? true
         :max-delete-count        10000
         :selected-items          selected
         :on-delete-clicked!      events/delete-selected-clicked}])]))

(defn create-props-atom
  [selected sorted-tps grid-store recompute-grid-sizes! hover-atom modal-atom]
  (let [tenant-schema @session/tenant-schema
        columns
        (vec (keep (fn [k]
                     (let [field-schema (registry/get-field-schema tenant-schema k)
                           label        (attribute/attribute-label tenant-schema k)]
                       (column {:label                 label
                                :attribute             k
                                :field-schema          field-schema
                                :filter-data           nil
                                :sorting-disabled?     false
                                :width                 (grid/get-saved-width (events/setup-args) k)
                                :hover-atom            hover-atom
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :flex-grow             1})))
                   (events/resolve-column-keys)))]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args)
      :grid-entity-ids         (->> sorted-tps
                                    (mapv :db/id)
                                    (into #{}))
      :selected                selected
      :header-height           50
      :select-width            select-width
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400) 75)
      :avail-height            (- (or (:height @app-db/window) 400) 75)
      :useDynamicRowHeight     false
      :row-height              40
      :row-count               (if (sequential? sorted-tps) (count sorted-tps) 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :link-to-entity-field    :transaction-process/short-name
      ;; Route the empty-state "Create your first item." button to the same
      ;; custom creation flow as the "New transaction process" toolbar button
      ;; (it carries the vehicle/template business logic); without :create-fn the
      ;; default no-rows button falls through to the generic entity creator.
      :no-rows-renderer        (r/partial grid/default-no-rows-render
                                          @db/transaction-processes
                                          :entity.type/transaction-process
                                          {:create-fn #(swap! db/modal-state assoc :create? true)})
      :on-row-click!           (fn [row]
                                 (let [data (when (< row (count sorted-tps))
                                              (nth sorted-tps row))]
                                   (routing/open-entity-type :entity.type/transaction-process
                                                             (:db/id data))))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args) k dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args)
                                                        dragged-column
                                                        after-column))
      :can-filter?             (fn [_k] true)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :all-selection!          (fn []
                                 (let [selected (:selected @db/transaction-processes)]
                                   (if (or (= :all selected) (seq selected))
                                     (grid/clear-selection! (events/setup-args))
                                     (swap! db/transaction-processes assoc :selected :all))))
      :select-action-renderer  toolbar-buttons-renderer
      :on-row-select!          (fn [index checked]
                                 (let [data    (when (< index (count sorted-tps))
                                                 (nth sorted-tps index))
                                       tp-id   (:db/id data)]
                                   (if (= selected :all)
                                     (grid/select-all-but (events/setup-args) tp-id)
                                     (if (boolean checked)
                                       (swap! db/transaction-processes assoc-in [:selected tp-id] true)
                                       (swap! db/transaction-processes potpuri/dissoc-in [:selected tp-id])))))
      :deselect-entity!        (fn [entity-id]
                                 (if (= selected :all)
                                   (grid/select-all-but (events/setup-args) entity-id)
                                   (swap! db/transaction-processes potpuri/dissoc-in [:selected entity-id])))
      :row-data-getter         (partial grid/default-row-data-getter sorted-tps selected)
      :new-action-view         {:in-use?    false
                                :modal-atom modal-atom}})))

(defn render
  [grid-store _args]
  (let [hover-atom            (r/atom {:hover {:col nil :row nil}})
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [_args]
      (let [tenant-schema       @session/tenant-schema
            the-setup-args      (events/setup-args)
            selected            (:selected @db/transaction-processes)
            sorted-tps          (:sorted-grid-entities @db/transaction-processes)
            grid-atom           (create-props-atom selected
                                                   sorted-tps
                                                   grid-store
                                                   recompute-grid-sizes!
                                                   hover-atom
                                                   db/modal-state)
            show-quick-filters? (:show-quick-filters? @db/transaction-processes)
            filters-props
            {:minimized?            (not show-quick-filters?)
             :hide-maximize?        true
             :universal-search?     true
             :search-placeholder    "Search transaction processes..."
             :setup-args            the-setup-args
             :set-universal-search! (r/partial grid/set-universal-search! the-setup-args)
             :set-name-search!      (r/partial grid/set-name-search! the-setup-args)
             :hide-named-views?     true
             :named-views           (events/named-views)
             :current-view          @db/view-settings
             :columns               (events/resolve-column-keys)
             :all-columns           (events/resolve-column-key-universe)
             :get-column-label      (fn [a]
                                      (attribute/attribute-label tenant-schema a))
             :set-filter!           (fn [k filter-data]
                                      (grid/set-filter (events/setup-args) k filter-data))
             :edit-filter!          (fn [k filter-data]
                                      (grid/open-filter-curtain
                                       {:field-key      k
                                        :field-schema   (registry/get-field-schema tenant-schema k)
                                        :options        (->> (events/resolve-column-key-universe)
                                                             (map (juxt identity
                                                                        (partial registry/get-field-schema
                                                                                 tenant-schema))))
                                        :current-filter filter-data
                                        :update-fn!     (fn [k filter-data]
                                                          (grid/set-filter (events/setup-args)
                                                                           k
                                                                           filter-data))}
                                       nil))
             :hide-qf-attributes    []
             :load-view!            (fn [named-view]
                                      (events/load-view! named-view)
                                      (grid/clear-selection! {:state db/transaction-processes}))
             :save-view!            events/save-current-view!
             :delete-view!          named-view-events/delete-view!
             :toggle-minimize!      (fn []
                                      (events/toggle-quick-filters!)
                                      (.setTimeout js/window recompute-grid-sizes! 1000))}]
        [:<>
         [grid-filters/view filters-props]
         [:div
          {:class "flex flex-auto ps-4"}
          [:div
           {:class-name (fu/cs "grid-view-content"
                               (when-not show-quick-filters? "named-views-minimized"))}
           (when (:create? @db/modal-state)
             [create-modal/view {:on-close #(swap! db/modal-state dissoc :create?)}])
           [scrollable-grid/view
            {:props-atom grid-atom
             :hover-atom hover-atom}]]]]))))

(defn toolbar
  []
  (let [read-only?  (session/is-read-only-user?)
        can-create? (and (not read-only?)
                         (or (session/is-admin?)
                             (seq (create-modal/fund-options))))]
    [:div
     {:class "px-4 py-1 flex items-center justify-between
              bg-base-light border-b border-solid border-base-light-border-paler"}
     [:div
      {:class "flex gap-2 items-center"}
      [:label
       {:class "font-semibold"}
       (tr :transaction-processes)]
      [:span
       {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold
                text-base-light-text bg-complement-regular rounded-full"}
       (count (:sorted-grid-entities @db/transaction-processes))]
      [grid-components/named-view-selector
       {:named-views               (events/named-views)
        :current-view              @db/view-settings
        :load-view!                (fn [id]
                                     (events/load-view! (events/named-view-by-id id))
                                     (grid/clear-selection! {:state db/transaction-processes}))
        :delete-view!              named-view-events/start-delete-view!
        :accounts-with-view-access (session/active-accounts-with-module-access
                                    :module/transaction-process)
        :save-current-view!        events/save-current-view!}]]
     [:div
      {:class "flex items-center gap-2"}
      (when can-create?
        [button/sm-primary-solid
         {:on-click #(swap! db/modal-state assoc :create? true)}
         "New transaction process"])
      (zap-toolbar/dropdown
       [{:label     (tr :action/select-columns)
         :add-ellipsis? true
         :on-select (fn [_] (grid/open-select-columns-curtain (events/setup-args)))}]
       true)]]))

(defn view
  [_args]
  (let [grid-store (atom {})
        close-atom (atom false)
        resizer    (fn []
                     (js/setTimeout (partial vu/recompute-grid-sizes-with-state! grid-store) 100))]
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn []
                                (fu/timeout 10 (partial events/setup close-atom)))
      :component-will-unmount (fn []
                                (reset! close-atom true)
                                (events/cleanup)
                                (.removeEventListener js/window "resize" resizer))
      :reagent-render         (partial render grid-store)})))

(defmethod routing/render-view :transaction-processes-grid
  [_]
  (if-not (session/transaction-process?)
    [not-found/not-found-view]
    (if (session/new-grid-2023?)
      [default-page-layout/view
       [:<>
        [transaction-process-listing/page-content]]]
      [default-page-layout/view
       [:<>
        [toolbar]
        [view]]])))
